import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface TorsadeBranchementDetailProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 140px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
`;

const ProductLayout = styled.div`
  display: flex;
  gap: 3rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const MainImage = styled.div`
  width: 100%;
  max-width: 600px;
  height: 600px;
  background: #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    max-width: 350px;
    height: 350px;
    padding: 1.5rem;
    align-self: center;
    margin: 1rem auto;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ThumbnailImage = styled.div`
  width: 80px;
  height: 80px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 1rem auto;
    
    img {
      max-width: 100px;
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 4px;
  }
`;

const ProductInfo = styled.div<{ $isRTL: boolean }>`
  flex: 1;
  padding: 1rem 0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const ProductTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 2.8rem;
  color: #0f172a;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ProductSubtitle = styled(motion.p)<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #64748b;
  margin-bottom: 2rem;
`;

const TechnicalDatasheet = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  width: 100%;
  max-width: 300px;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
`;

const DatasheetIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;

  img {
    filter: brightness(0) invert(1);
    width: 24px;
    height: 24px;
  }
`;

const DatasheetText = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const DatasheetTitle = styled.span<{ $isRTL: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
`;

const DatasheetFormat = styled.span<{ $isRTL: boolean }>`
  font-size: 0.875rem;
  color: #64748b;
`;



const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 1rem auto;
    
    img {
      max-width: 100px;
    }
  }
  
  img {
    max-width: 120px;
    height: auto;
    object-fit: contain;
  }
`;

const ContentSection = styled.div<{ $isRTL: boolean }>`
  margin-top: 4rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 2.2rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 2px;
  }
`;


const DescriptionSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const DescriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-top: 1rem;
  }
`;

const DescriptionCard = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const CardLabel = styled.div<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const CardSubTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #0f172a;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const CardValue = styled.div<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const CardText = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #475569;
  line-height: 1.7;
  margin: 0;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;


const ResourcesSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const ResourcesContent = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  position: relative;
`;

const ResourcesSubTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

// Products section styled components
const ProductsSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
  
    &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ItemReference = styled.p<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailRow = styled.div<{ $isRTL: boolean }>`
  display: flex;
  gap: 0.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const DetailLabel = styled.span<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
  min-width: 200px;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  @media (max-width: 768px) {
    min-width: 150px;
  }
`;

const DetailValue = styled.span<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ItemDatasheet = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
  
    &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    min-width: auto;
    width: 100%;
  }
`;



const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;
const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.$active ? '#dc2626' : '#e2e8f0'};
  background: ${props => props.$active ? '#dc2626' : '#f8fafc'};
  color: ${props => props.$active ? 'white' : '#1e293b'};
  font-weight: ${props => props.$active ? '600' : 'normal'};
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
  
    &:hover {
    background: ${props => props.$active ? '#b91c1c' : '#f1f5f9'};
    border-color: ${props => props.$active ? '#b91c1c' : '#cbd5e1'};
  }
`;
const DocumentationSection = styled.div<{ $isRTL: boolean }>`
  margin-bottom: 2rem;
`;

const DocumentationItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
  
    &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
  }
`;

const DocumentationInfo = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
`;

const DocumentationTitle = styled.span<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #0f172a;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const DocumentationSize = styled.span<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
`;

const DownloadButton = styled.button<{ $isRTL: boolean }>`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
  
    &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
  }
`;

const TorsadeBranchementDetail: React.FC<TorsadeBranchementDetailProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Transport et distribution de l'énergie", path: "/category/cables-transport" },
    { label: t.lowVoltageCablesTitle, path: "/category/cables-transport" },
    { label: "Torsadé de branchement", path: "/product/torsade-branchement" },
  ];




  const product = {
    title: 'Torsadé de branchement',
    subtitle: 'Câbles basse tension pour branchement aérien',
    image: "/images/catalog/Câbles de transport et de distribution de l'énergie/Torsadé de branchement.jpg",
    datasheet: "/images/catalog/Câbles de transport et de distribution de l'énergie/Torsadé de branchement/Torsadé de branchement.pdf",
  };


  const products = [
    {
      id: "torsade-bt-2x16-alu-1",
      name: "TORSADE BT 2X16 ALU",
      reference: "Réf. Nexans 56870252000",
      conditionnement: "m",
      nature: "Aluminium",
      section: "16 mm²",
      conducteurs: "2",
      diametre: "15,6 mm",
      filename: "TORSADE BT 2X16 ALU.pdf"
    },
    {
      id: "torsade-bt-2x16-alu-2",
      name: "TORSADE BT 2X16 ALU",
      reference: "Réf. Nexans 56870252003",
      conditionnement: "m",
      nature: "Aluminium",
      section: "16 mm²",
      conducteurs: "2",
      diametre: "- mm",
      filename: "TORSADE BT 2X16 ALU (2).pdf"
    },
    {
      id: "torsade-bt-2x25-alu",
      name: "TORSADE BT 2X25 ALU",
      reference: "Réf. Nexans 56870256700",
      conditionnement: "m",
      nature: "Aluminium",
      section: "25 mm²",
      conducteurs: "2",
      diametre: "18,8 mm",
      filename: "TORSADE BT 2X25 ALU.pdf"
    },
    {
      id: "torsade-bt-4x16-alu",
      name: "TORSADE BT 4X16 ALU",
      reference: "Réf. Nexans 56870452800",
      conditionnement: "m",
      nature: "Aluminium",
      section: "16 mm²",
      conducteurs: "4",
      diametre: "18,8 mm",
      filename: "TORSADE BT 4X16 ALU.pdf"
    },
    {
      id: "torsade-bt-4x25-alu",
      name: "TORSADE BT 4X25 ALU",
      reference: "Réf. Nexans 56870456600",
      conditionnement: "m",
      nature: "Aluminium",
      section: "25 mm²",
      conducteurs: "4",
      diametre: "420,0 mm",
      filename: "TORSADE BT 4X25 ALU.pdf"
    }
  ];

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 5;
  const totalPages = Math.ceil(products.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleProductDatasheet = (filename: string) => {
    const filePath = `/images/catalog/Câbles de transport et de distribution de l'énergie/Torsadé de branchement/produits/${filename}`;
    window.open(filePath, '_blank');
  };
  const handleDatasheetClick = () => {
    window.open(product.datasheet, '_blank');
  };


  const handleDownload = (filename: string) => {
    const filePath = `/images/catalog/Câbles de transport et de distribution de l'énergie/Cable MT NFC 33-226 1220 (24) kV/${filename}`;
    window.open(filePath, '_blank');
  };

  return (
    <ProductDetailPage $isRTL={isRTL} ref={ref}>
      <Container>
        <Breadcrumbs 
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.label}>
              <BreadcrumbLink 
                $isLast={index === breadcrumbs.length - 1} 
                onClick={() => index !== breadcrumbs.length - 1 && navigate(crumb.path)}
              >
                {crumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && (
                <span>{isRTL ? '<' : '>'}</span>
              )}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <ProductLayout>
          <ImageSection>
            <MainImage>
              <img src={product.image} alt={product.title} />
            </MainImage>
            <ThumbnailImage>
              <img src={product.image} alt={`${product.title} thumbnail`} />
            </ThumbnailImage>
          </ImageSection>

          <ProductInfo $isRTL={isRTL}>
            <ProductTitle 
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {product.title}
            </ProductTitle>

            <ProductSubtitle 
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {product.subtitle}
            </ProductSubtitle>

            <TechnicalDatasheet 
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
            >
              <DatasheetIcon>
                <img 
                  src="/images/catalog/pdf.svg" 
                  alt="PDF icon" 
                />
              </DatasheetIcon>
              <DatasheetText $isRTL={isRTL}>
                <DatasheetTitle $isRTL={isRTL}>Fiche technique</DatasheetTitle>
                <DatasheetFormat $isRTL={isRTL}>PDF</DatasheetFormat>
              </DatasheetText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ProductInfo>

        </ProductLayout>

        <ContentSection $isRTL={isRTL}>
          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Produits
          </SectionTitle>
          <ProductsSection $isRTL={isRTL}>
            <ProductsList>
              {currentProducts.map((item) => (
                <ProductItem key={item.id} $isRTL={isRTL}>
                  <ItemInfo>
                    <ItemName $isRTL={isRTL}>{item.name}</ItemName>
                    <ItemReference $isRTL={isRTL}>{item.reference}</ItemReference>
                    <ItemDetails>
                      <DetailRow $isRTL={isRTL}>
                        <DetailLabel $isRTL={isRTL}>Conditionnement:</DetailLabel>
                        <DetailValue $isRTL={isRTL}>{item.conditionnement}</DetailValue>
                      </DetailRow>
                      <DetailRow $isRTL={isRTL}>
                        <DetailLabel $isRTL={isRTL}>Nature de l'âme:</DetailLabel>
                        <DetailValue $isRTL={isRTL}>{item.nature}</DetailValue>
                      </DetailRow>
                      <DetailRow $isRTL={isRTL}>
                        <DetailLabel $isRTL={isRTL}>Section du conducteur:</DetailLabel>
                        <DetailValue $isRTL={isRTL}>{item.section}</DetailValue>
                      </DetailRow>
                      <DetailRow $isRTL={isRTL}>
                        <DetailLabel $isRTL={isRTL}>Nombre de conducteurs:</DetailLabel>
                        <DetailValue $isRTL={isRTL}>{item.conducteurs}</DetailValue>
                      </DetailRow>
                    </ItemDetails>
                  </ItemInfo>
                  <ItemDatasheet 
                    $isRTL={isRTL}
                    onClick={() => handleProductDatasheet(item.filename)}
                  >
                    <DatasheetIcon>
                      <img 
                        src="/images/catalog/pdf.svg" 
                        alt="PDF icon" 
                      />
                    </DatasheetIcon>
                    <DatasheetText $isRTL={isRTL}>
                      <DatasheetTitle $isRTL={isRTL}>Fiche technique</DatasheetTitle>
                      <DatasheetFormat $isRTL={isRTL}>PDF</DatasheetFormat>
                    </DatasheetText>
                  </ItemDatasheet>
                </ProductItem>
              ))}
            </ProductsList>
            
            <PaginationContainer>
              {currentPage > 1 && (
                <PaginationButton onClick={() => handlePageChange(currentPage - 1)}>
                  Précédent
                </PaginationButton>
              )}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationButton
                  key={page}
                  $active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PaginationButton>
              ))}
              {currentPage < totalPages && (
                <PaginationButton onClick={() => handlePageChange(currentPage + 1)}>
                  Suivant
                </PaginationButton>
              )}
            </PaginationContainer>
          </ProductsSection>

          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Description
          </SectionTitle>
          <DescriptionSection $isRTL={isRTL}>
            <DescriptionGrid>
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Normes</CardTitle>
                <CardLabel $isRTL={isRTL}>PRODUCT</CardLabel>
                <CardValue $isRTL={isRTL}>AEIC CS 6</CardValue>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Description</CardTitle>
                <CardLabel $isRTL={isRTL}>Désignation Internationale</CardLabel>
                <CardValue $isRTL={isRTL}>FR-N20XA8E</CardValue>
                <CardText $isRTL={isRTL}>
                  Cable MT à Enterrabilité Directe renforcée
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Normes</CardTitle>
                <CardLabel $isRTL={isRTL}>Internationales</CardLabel>
                <CardValue $isRTL={isRTL}>HD 620</CardValue>
                <CardLabel $isRTL={isRTL}>Nationales</CardLabel>
                <CardValue $isRTL={isRTL}>NF C 33-226</CardValue>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Description</CardTitle>
                <CardSubTitle $isRTL={isRTL}>Utilisation:</CardSubTitle>
                <CardText $isRTL={isRTL}>
                  Les câbles NF C 33-226 à Enterrabilité Directe Renforcée (EDR) sont destinés à la distribution publique moyenne tension HTA 12/20 kV. La fonction d'enterrabilité Directe est assurée par une gaine polyéthylène supplémentaire extrudée au dessus de la phase.
                </CardText>
                <CardText $isRTL={isRTL}>
                  Les cables sont classés Non propagateur de la flamme selon NF EN 60332-1-2. Ils sont aussi classés AD8, AF2 et AN3 au sens de la norme NF C 13-200.
                </CardText>
                <CardText $isRTL={isRTL}>
                  Les gaines extérieures des cables sont résistantes aux termites.
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Description</CardTitle>
                <CardText $isRTL={isRTL}>
                  Ils peuvent être constitués de 3 conducteurs de phase assemblés en torsade.
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Désignation commerciale</CardTitle>
                <CardText $isRTL={isRTL}>
                  Les cables sont désignés : EDRMAX by Nexans™
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Conditions de pose</CardTitle>
                <CardLabel $isRTL={isRTL}>Profondeur de pose</CardLabel>
                <CardValue $isRTL={isRTL}>0.80 m</CardValue>
              </DescriptionCard>
            </DescriptionGrid>
          </DescriptionSection>


          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Ressources
          </SectionTitle>
          <ResourcesSection $isRTL={isRTL}>
            <ResourcesContent $isRTL={isRTL}>
              <DocumentationSection $isRTL={isRTL}>
                <ResourcesSubTitle $isRTL={isRTL}>Documentation</ResourcesSubTitle>
                <DocumentationItem $isRTL={isRTL}>
                  <DocumentationInfo $isRTL={isRTL}>
                    <DocumentationTitle $isRTL={isRTL}>Catalogue MT</DocumentationTitle>
                    <DocumentationSize $isRTL={isRTL}>PDF - 3.2 MB</DocumentationSize>
                  </DocumentationInfo>
                  <DownloadButton 
                    $isRTL={isRTL}
                    onClick={() => handleDownload('Catalogue MT.pdf')}
                  >
                    Télécharger
                  </DownloadButton>
                </DocumentationItem>
              </DocumentationSection>

            </ResourcesContent>
          </ResourcesSection>
        </ContentSection>
      </Container>
    </ProductDetailPage>
  );
};

export default TorsadeBranchementDetail;
